import type {
  SelectedFilterOption,
  TimeBucketOption,
} from 'features/charts-v2/types'
import { TIME_BUCKET_CHART_TYPES } from 'features/charts-v2/types/chart-types'
import type { ChartPanel } from 'features/charts-v2/types/filter-hash/v2'
import { foldRatingOnlyFilter } from 'features/charts-v2/utils/fold-rating-only-filter'
import { convertFilterOption } from 'features/charts-v2/utils/translate-v2-to-v1-filters'
import type {
  AppliedFilterOption,
  AppliedFilterOptionRange,
  AppliedFilterOptionSelect,
} from 'features/filters/types'
import { HardcodedFilterOptionsEnum } from 'features/filters/utils/helpers'
import type { Filters, SearchTermsLogical } from 'types/project'

const TIME_BUCKET_OPTIONS: ReadonlySet<TimeBucketOption> = new Set([
  'day',
  'week',
  'month',
  'quarter',
  'year',
])

/**
 * Narrow a persisted V1 `timeInterval` string to a valid TimeBucketOption.
 * Returns null for absent/unknown values so the caller falls back to the
 * existing panel's bucket, mirroring the CREATE_TIME → dateRange fallback.
 */
function toTimeBucket(
  value: string | null | undefined,
): TimeBucketOption | null {
  return value && TIME_BUCKET_OPTIONS.has(value as TimeBucketOption)
    ? (value as TimeBucketOption)
    : null
}

/**
 * Type guard for AppliedFilterOptionSelect
 */
function isAppliedFilterOptionSelect(
  option: AppliedFilterOption,
): option is AppliedFilterOptionSelect {
  return 'values' in option && !('min' in option) && !('max' in option)
}

/**
 * Type guard for AppliedFilterOptionRange
 */
function isAppliedFilterOptionRange(
  option: AppliedFilterOption,
): option is AppliedFilterOptionRange {
  return 'min' in option && 'max' in option && 'value_type' in option
}

/**
 * Converts a V1 AppliedFilterOption to a V2 SelectedFilterOption.
 *
 * V1 sentiment special case: the `'0'` sentinel in `sentiment.values` signals
 * "Review Sentiment" (POST_AVG_SENTIMENT). V2 uses a separate field for this:
 * `post_sentiment`. Strip the sentinel and re-route to the correct field so
 * saved v1 charts open with the right sentiment mode in v2.
 *
 * V1 has no way to represent an "is not" (negation) operator — it flattens
 * both `is` and `is not` to the same bare `{field, values}` shape. So a v1
 * option round-tripping back to v2 is NOT evidence the user changed the
 * operator to `is`; it is only evidence v1 cannot say otherwise. `priorOperatorByField`
 * carries forward whatever non-`is` operator the field had on the existing
 * panel, so an "is not" filter survives an edit made through the v1 controls
 * instead of silently inverting to "is".
 */
function convertAppliedFilterToSelectedFilter(
  option: AppliedFilterOption,
  priorOperatorByField: ReadonlyMap<string, SelectedFilterOption['operator']>,
): SelectedFilterOption | null {
  if (isAppliedFilterOptionSelect(option)) {
    // Handle v1 'sentiment' filter with the '0' (Review Sentiment) sentinel.
    if (option.field === 'sentiment' && option.values.includes('0')) {
      const valuesWithoutSentinel = option.values.filter((v) => v !== '0')
      // '0' alone means "Review Sentiment, no value filter" — keep the field
      // but route to post_sentiment so the mode shows correctly.
      return {
        field: 'post_sentiment',
        operator: priorOperatorByField.get('post_sentiment') ?? 'is',
        values: valuesWithoutSentinel,
      }
    }
    return {
      field: option.field,
      operator: priorOperatorByField.get(option.field) ?? 'is',
      values: option.values,
    }
  }

  if (isAppliedFilterOptionRange(option)) {
    return {
      field: option.field,
      operator: priorOperatorByField.get(option.field) ?? 'is',
      values:
        (option as AppliedFilterOptionRange & { values?: string[] }).values ??
        [],
      min: option.min,
      max: option.max,
      valueType: option.value_type,
    }
  }

  return null
}

/**
 * Map a v1 `searchCondition` array to the v2 keyword-card operator label.
 *
 * V1 `searchCondition` may carry a join operator ("or"/"and"), a negation
 * ("not"), or the legacy per-term field ("body"). The mapping mirrors the v1
 * backend's `build_sql_search_query`: an explicit "or" (without "and") is OR
 * ("Includes some"); "and" — or no join token at all — defaults to AND
 * ("Includes all"); "not" turns it into the matching exclude operator (NOT(AND)
 * → "Excludes all", which requires an explicit "and"; otherwise NOT(OR) →
 * "Excludes some"). This is intentionally NOT shared with the store's legacy
 * migration helper: that path consumes v2 join-operator arrays whose default is
 * OR, whereas this path must default to AND to match the v1 backend.
 */
function v1JoinToOperatorLabel(
  searchCondition: string[],
): 'Includes all' | 'Includes some' | 'Excludes some' | 'Excludes all' {
  const lowered = searchCondition.map((token) => token.toLowerCase())
  const isNot = lowered.includes('not')
  const hasAnd = lowered.includes('and')
  const hasOr = lowered.includes('or')
  if (isNot) {
    return hasAnd ? 'Excludes all' : 'Excludes some'
  }
  return hasAnd || !hasOr ? 'Includes all' : 'Includes some'
}

/**
 * Map a v1 `search_terms_logical` block to v2 keyword-card operator labels.
 *
 * Inverse of `buildSearchTermsLogical` in translate-v2-to-v1-filters, so a
 * keyword search round-trips through the panel: include_all → "Includes all",
 * include_any → "Includes some", exclude_all (NOT-OR) → "Excludes some",
 * exclude_all_of (NOT-AND) → "Excludes all". Empty groups are dropped.
 */
export function buildSearchCriteriaFromLogical(
  logical: SearchTermsLogical | null | undefined,
): Record<string, string[]> {
  const criteria: Record<string, string[]> = {}
  if (logical?.include_all?.length) {
    criteria['Includes all'] = [...logical.include_all]
  }
  if (logical?.include_any?.length) {
    criteria['Includes some'] = [...logical.include_any]
  }
  if (logical?.exclude_all?.length) {
    criteria['Excludes some'] = [...logical.exclude_all]
  }
  if (logical?.exclude_all_of?.length) {
    criteria['Excludes all'] = [...logical.exclude_all_of]
  }
  return criteria
}

/**
 * Converts V1 Filters to a partial ChartPanel update.
 * Reverse of translatePanelToV1Filters.
 *
 * @param filters - V1 Filters object
 * @param existingPanel - Current panel state (used to preserve non-filter fields)
 * @returns Partial ChartPanel with updated filters, dateRange, and searchTerms
 */
export function translateV1FiltersToPanel(
  filters: Filters,
  existingPanel: ChartPanel,
): Partial<ChartPanel> {
  const updates: Partial<ChartPanel> = {}

  // 1. Extract date range from CREATE_TIME filter
  const dateFilter = filters.values.find(
    (f) => f.field === HardcodedFilterOptionsEnum.CREATE_TIME,
  )

  if (dateFilter && isAppliedFilterOptionRange(dateFilter)) {
    updates.dateRange = {
      startDate: dateFilter.min,
      endDate: dateFilter.max,
    }
  } else {
    // Preserve existing date range if no CREATE_TIME in filters
    updates.dateRange = existingPanel.dateRange
  }

  // 1b. Restore the time-bucket granularity persisted by translatePanelToV1Filters.
  // Mirrors the dateRange handling above: use the persisted value when present
  // and valid, otherwise preserve the existing panel's bucket (so a rebuild from
  // V1 filters no longer silently reverts it to the preset default).
  //
  // Only adopt it onto a panel whose chart type actually has a time axis
  // (TIME_BUCKET_CHART_TYPES). `timeInterval` rides along in every page's v1
  // projection regardless of chart type, but every OTHER strategy (Bar,
  // StackedBar, Pie, Treemap, ...) forwards a non-null `timeBucket` straight
  // into its sort comparator, which then parses category labels (e.g. theme
  // names) as dates and silently stops sorting by count. A Composition panel
  // must keep its own (correctly null) bucket no matter what another page's
  // Line/Area panel persisted.
  const persistedTimeBucket = toTimeBucket(filters.timeInterval)
  updates.timeBucket = TIME_BUCKET_CHART_TYPES.includes(existingPanel.chartType)
    ? (persistedTimeBucket ?? existingPanel.timeBucket)
    : existingPanel.timeBucket

  // 2. Convert facet filters (exclude CREATE_TIME which is handled separately)
  const facetFilters = filters.values.filter(
    (f) => f.field !== HardcodedFilterOptionsEnum.CREATE_TIME,
  )

  // v1 cannot represent every v2 filter shape (numeric filters — record_count/
  // sentence_count/sentiment_filter — carry numericValue with no `values`, so
  // convertFilterOption returns null for them) and it flattens `is not` to a
  // bare select. Never let the lossy v1 leg of the round trip delete or invert
  // what the panel already had:
  //   - filters v1 can't express at all are carried forward untouched.
  //   - the operator for fields v1 does emit but can't distinguish is restored
  //     from the existing panel via priorOperatorByField.
  const preservedFilters = existingPanel.filters.filter(
    (f) => convertFilterOption(f) === null,
  )
  const priorOperatorByField = new Map(
    existingPanel.filters
      .filter((f) => f.operator !== 'is')
      .map((f) => [f.field, f.operator] as const),
  )

  const selectedFilters: SelectedFilterOption[] = []
  facetFilters.forEach((filter) => {
    const converted = convertAppliedFilterToSelectedFilter(
      filter,
      priorOperatorByField,
    )
    if (converted) {
      selectedFilters.push(converted)
    }
  })
  // v1 speaks rating-only as its own filter; fold it back for the control.
  updates.filters = foldRatingOnlyFilter([
    ...preservedFilters,
    ...selectedFilters,
  ])

  // 3. Convert the v1 search into the v2 panel's keyword cards. Three input
  // shapes, in precedence order:
  //   (a) semantic — searchCondition ["SEMANTIC"]; phrase in searchTerms.
  //   (b) logical keyword panel — keywords in `search_terms_logical`
  //       (the panel clears searchQuery/searchCondition when it applies).
  //   (c) legacy parallel arrays — searchQuery[] + a searchCondition[] join
  //       operator ("or"/"and"/"not") or per-term field ("body").
  const query = filters.searchQuery ?? []
  const hasQuery = query.length > 0
  const logicalCriteria = buildSearchCriteriaFromLogical(
    filters.search_terms_logical,
  )
  // A SEMANTIC marker only counts as an active smart search when there is an
  // actual query phrase. A stale `searchCondition: ["SEMANTIC"]` with no query
  // (left behind by a prior smart search, e.g. a dashboard's global filter that
  // later switched to logical keywords) must NOT win precedence here — doing so
  // zeroed `searchCriteria` and silently dropped the logical keywords while the
  // (empty) semantic search produced no filtering at all.
  const isSemanticV1 =
    (filters.searchCondition?.includes('SEMANTIC') ?? false) && hasQuery

  if (isSemanticV1) {
    // Smart-search box: one space-joined phrase, no keyword cards.
    updates.smartSearchEnabled = true
    updates.searchTerms = hasQuery ? [query.join(' ')] : []
    updates.searchCriteria = {}
  } else if (Object.keys(logicalCriteria).length > 0) {
    // Logical keyword panel: keywords already grouped by operator in
    // `search_terms_logical`. Route them straight into the cards. Without this,
    // an edit via the keyword panel (which sends only `search_terms_logical`,
    // with empty searchQuery/searchCondition) silently dropped the keywords.
    updates.smartSearchEnabled = false
    updates.searchTerms = []
    updates.searchCriteria = logicalCriteria
  } else {
    // Lexical search lives in the keyword cards: keywords under the operator
    // label DERIVED from searchCondition, with searchTerms cleared (that field
    // is the semantic phrase box). Previously this hard-coded "Includes all"
    // and discarded searchCondition, so a v1 "or" search silently became AND
    // and the keywords were also stranded in searchTerms.
    updates.smartSearchEnabled = false
    updates.searchTerms = []
    updates.searchCriteria = hasQuery
      ? { [v1JoinToOperatorLabel(filters.searchCondition ?? [])]: [...query] }
      : {}
  }

  return updates
}
